import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from "@mui/material";
import { previewFileTypes } from "../../context/model";
import { REACT_APP_WUCA_DXP_ADMIN_URL } from "../../services/config";
import "./ResourceAccordion.scss";
interface FileItem {
  fileName: string;
  fileText?: string;
  fileid: string;
  filePath: string;
  fileType: string;
  externalURL?: string;
}

interface ChildFolder {
  folderName: string;
  orderBy: number | null;
  files: FileItem[];
  accordionOpen?: boolean;
}

interface ParentFolder {
  parentFolder: string;
  orderBy: number | null;
  accordionOpen?: boolean;
  childFolders: ChildFolder[];
}

interface ResourceAccordionProps {
  pageName?: string;
  data: ParentFolder[];
  parentExpanded: String[];
  childExpanded: String[];
  onParentChange: (panel: string) => (_event: React.ChangeEvent<{}>, isExpanded: boolean) => void;
  onChildChange: (panel: string) => (_event: React.ChangeEvent<{}>, isExpanded: boolean) => void;
  onPreview: (file: FileItem) => void;
  onDownload: (file: FileItem) => void;
}

const ResourceAccordion: React.FC<ResourceAccordionProps> = ({
  pageName,
  data,
  parentExpanded,
  childExpanded,
  onParentChange,
  onChildChange,
  onPreview,
  onDownload,
}) => {
  // URL sanitization function
  const sanitizeUrl = (url: string): string => {
    if (!url) return "";

    const trimmedUrl = url.trim();
    if (trimmedUrl === "") return "";

    // Check if it's already a valid HTTP/HTTPS URL
    if (trimmedUrl.toLowerCase().startsWith("http://") || trimmedUrl.toLowerCase().startsWith("https://")) {
      return trimmedUrl;
    }

    // Check if it starts with www and add https://
    if (trimmedUrl.toLowerCase().startsWith("www.")) {
      return `https://${trimmedUrl}`;
    }

    // For other cases, assume it needs https://
    return `https://${trimmedUrl}`;
  };

  const isExternalUrl = (url: string): boolean => {
    if (!url) return false;
    const sanitizedUrl = sanitizeUrl(url);
    return sanitizedUrl.toLowerCase().startsWith("http://") || sanitizedUrl.toLowerCase().startsWith("https://");
  };

  return (
    <>
      {data.map((item, i) => (
        <Accordion
          id={`panel-${i}`}
          expanded={parentExpanded.includes(`panel-${i}`)}
          onChange={onParentChange(`panel-${i}`)}
          key={`panel-${i}`}
          className="accordion-parent-container"
        >
          <AccordionSummary
            aria-controls={`panel-${i}-content`}
            id={`panel-${i}-header`}
            className="accordion-panel-header"
          >
            <div className="accordion-panel-header-container">
              <div className="accordion-panel-header-icon">
                <i
                  className={`fa-kit fa-chevron-with-circle-${parentExpanded.includes(`panel-${i}`) ? "down" : "right"
                    }`}
                ></i>
              </div>
              <div className="accordion-panel-header-bg">
                <p className="accordion-panel-header-text">
                  {item.parentFolder}
                </p>
              </div>
            </div>
          </AccordionSummary>
          <AccordionDetails className="accordion-parent-panel-details">
            {item.childFolders.map((childFolder, j) => (
              <Accordion
                id={`child-panel-${i}-${j}`}
                expanded={childExpanded.includes(`child-panel-${i}-${j}`)}
                onChange={onChildChange(`child-panel-${i}-${j}`)}
                key={`child-panel-${i}-${j}`}
                className="accordion-child-container"
              >
                <AccordionSummary
                  aria-controls={`child-panel-${i}-${j}-content`}
                  id={`child-panel-${i}-${j}-header`}
                  className="accordion-child-panel-header"
                >
                  <div className="accordion-child-panel-header-container">
                    <div className="accordion-child-panel-header-icon">
                      <i
                        className={`fa-kit fa-chevron-small-${childExpanded.includes(`child-panel-${i}-${j}`) ? "down" : "right"
                          }`}
                      ></i>
                    </div>
                    <div className="accordion-child-panel-header-bg">
                      <p className="accordion-child-panel-header-text">
                        {childFolder.folderName}
                      </p>
                    </div>
                  </div>
                </AccordionSummary>
                <AccordionDetails className="accordion-child-panel-details">
                  <ul>
                    {childFolder.files.map((file, k) => {
                      const hasExternalUrl = file.externalURL?.toLowerCase() && file.externalURL?.toLowerCase().trim() !== "";
                      const sanitizedUrl = hasExternalUrl ? sanitizeUrl(file.externalURL!) : "";

                      const previewType = previewFileTypes[file.fileType || ""];
                      // MyDocuments only supports inline preview for image/pdf; other pages allow every mapped type
                      const canPreview = pageName === "MyDocuments"
                        ? previewType === "image" || previewType === "pdf"
                        : !!previewType;

                      return (
                        <li key={k}>
                          <div className="accordion-child-panel-details-item">
                            {hasExternalUrl ? (
                              <a
                                href={sanitizedUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="item-link external-link"
                              >
                                {file.fileName}
                              </a>
                            ) : (
                              <p className="item-link">{file.fileName}</p>
                            )}
                            {file.fileText && <p className="item-text">{file.fileText}</p>}
                          </div>
                          <div className="accordion-child-panel-details-action">
                            {!hasExternalUrl && file.filePath && (
                              <>
                                {canPreview  && (
                                  <button
                                    className="file-preview-icon"
                                    onClick={() => onPreview(file)}
                                  >
                                    <i className="fa-kit fa-view"></i>
                                  </button>
                                )}
                                <button
                                    className="file-download-icon"
                                    onClick={() => onDownload(file)}
                                  >
                                    <i className="fa-kit fa-download"></i>
                                  </button>
                              </>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </AccordionDetails>
              </Accordion>
            ))}
          </AccordionDetails>
        </Accordion>
      ))}
    </>
  );
};

export default ResourceAccordion;
